import { useState, useEffect, useCallback, useRef } from "react";
import { TextSelection } from "prosemirror-state";
import { useSpeechRecognition } from "./useSpeechRecognition";
import { useAudioRecording } from "./useAudioRecording";
import { matchExactUndoRedo, processVoiceTranscript } from "../../../utils/voiceCommands";
import { findEditReportTrigger } from "../../../cre/editReportAi/editReportTrigger";
import { findMatchingHeading } from "../../../utils/voiceHeadingEngine";
import {
  collectEditorTextBlocks,
  findBlockIndexAtPos,
  findCursorTextBlock,
  getCurrentSentenceRange,
  getFirstSentenceRange,
  getLastSentenceRange,
  getNextSentenceRange,
  getPreviousSentenceRange,
  listParagraphBlockRanges,
  findParagraphBlockRangeIndex,
} from "./rteSentenceSelection";
import { getSidebarDictationSttMode } from "../sidebarSttDisplay";

/** Whether free dictation at the caret should be prefixed with a space. */
function needsLeadingDictationSpace(editor) {
  if (!editor?.state) return false;
  const { from, to } = editor.state.selection;
  if (from !== to) return false;
  if (from <= 0) return false;

  const $from = editor.state.doc.resolve(from);
  if ($from.parentOffset === 0) return false;

  const charBefore = editor.state.doc.textBetween(from - 1, from);
  return Boolean(charBefore) && !/\s/.test(charBefore);
}

/**
 * hook for working with editor and speech recognition
 * @param {React.RefObject} editorRef - editor ref
 * @param {string} initialContent - initial content of editor
 * @param {string} language - language code for dictation (e.g., "en-US", "es-ES")
 * @param {string} anathomy - anatomy type code (e.g., "neuro_brain")
 * @returns {Object} - state and methods for working with editor and speech recognition
 */

const useRteWithSpeech = ({
  editorRef,
  initialContent = "",
  language = "en-US",
  anathomy = "",
  macros = [],
  onVoiceMacroResult,
  // When true, only recognized voice commands/actions will run.
  // Unrecognized speech will not be inserted as free dictation text.
  commandMode = false,
  // CRE AI Voice Editing. aiEditMode: speech is an edit instruction (only an
  // exact "undo"/"redo" runs locally) and goes to onAiEditText.
  aiEditMode = false,
  onAiEditText,
  // Dictation only: "ערוך דוח" / "Edit report" → onEditReportTrigger(rest of the sentence).
  onEditReportTrigger,
}) => {
  const [transcript, setTranscript] = useState(initialContent);
  const [isTranscriptInitialized, setIsTranscriptInitialized] = useState(
    !!initialContent
  );
  const [isMicActive, setIsMicActive] = useState(false);

  const addRecordedTranscriptRef = useRef(null);

  const handleStreamExpiredRef = useRef(() => {});

  const {
    streamingTranscript,
    setStreamingTranscript,
    connectSocket,
    disconnectSocket,
    socketRef, // Socket reference for sending language/type
  } = useSpeechRecognition({
    onFinalSegment: (payload) => {
      const segment =
        typeof payload === "string" ? payload : payload?.transcript ?? "";
      if (segment) {
        addRecordedTranscriptRef.current?.(segment);
      }
      setStreamingTranscript("");
    },
    onStreamExpired: () => handleStreamExpiredRef.current(),
  });

  const { toggleRecording, stopRecording, isRecording } = useAudioRecording({
    connectSocket,
    disconnectSocket,
    onError: (error) => {
      console.error("Recording error:", error);
    },
    language: language, // Pass dictation language
    anathomy: anathomy, // Pass anatomy type
  });

  const resetEditorTranscript = useCallback(() => {
    setStreamingTranscript("");
  }, [setStreamingTranscript]);

  /**
   * If the current selection is not in view, smoothly scroll the editor so it is.
   * Uses block: "center" so the selection is not flush to the edge—a line or two
   * above and below stay visible for context.
   */
  const scrollSelectionIntoViewIfNeeded = useCallback((editorInstance) => {
    if (!editorInstance?.view) return;
    const { state, view } = editorInstance;
    const { selection } = state;
    const { from } = selection;

    requestAnimationFrame(() => {
      try {
        const dom = view.domAtPos(from);
        const node = dom.node;
        const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
        }
      } catch (_) {
        // ignore
      }
    });
  }, []);

  // --- "Go to Heading" with exact match and move caret under heading ---

  const selectContentUnderHeader = useCallback(
    (headerName) => {
      const editor = editorRef.current?.editor;
      if (!editor || !editor.commands) {
        console.error(
          "❌ [selectContentUnderHeader] Editor not available or not Tiptap"
        );
        return false;
      }

      try {
        const { state } = editor;
        const { doc } = state;

        const headings = [];
        doc.descendants((node, pos) => {
          if (node.type.name.startsWith("heading")) {
            const level = node.attrs.level || 1;
            const text = node.textContent || "";
            headings.push({ node, pos, level, text });
          }
        });

        const targetHeading = findMatchingHeading(
          headerName,
          language,
          headings,
        );

        if (!targetHeading) {
          return false;
        }

        // Find next heading boundary (same or higher level) so we only search
        // within the section under the target heading.
        const targetLevel = targetHeading.level;
        let nextHeadingPos = null;

        for (
          let i = headings.indexOf(targetHeading) + 1;
          i < headings.length;
          i++
        ) {
          const nextHeading = headings[i];
          if (nextHeading.level <= targetLevel) {
            nextHeadingPos = nextHeading.pos;
            break;
          }
        }

        const headingEndPos = targetHeading.pos + targetHeading.node.nodeSize;
        const scopeEnd =
          nextHeadingPos !== null ? nextHeadingPos : doc.content.size;

        // Find first paragraph under the heading and place caret at its start.
        // Requirement: do NOT highlight/select the whole paragraph.
        let cursorPos = null;
        try {
          doc.nodesBetween(headingEndPos, scopeEnd, (node, pos) => {
            if (cursorPos != null) return false;
            if (node.type.name === "paragraph") {
              // For block nodes, caret inside inline content typically starts at pos + 1.
              cursorPos = pos + 1;
              return false;
            }
            return true;
          });
        } catch (_) {
          // ignore
        }

        if (cursorPos == null) {
          cursorPos = Math.max(
            1,
            Math.min(headingEndPos, doc.content.size)
          );
        }

        // Avoid placing caret at a hard block boundary (ProseMirror warnings).
        try {
          const $cursor = doc.resolve(cursorPos);
          if ($cursor.parent && !$cursor.parent.inlineContent) {
            cursorPos = Math.min(cursorPos + 1, doc.content.size);
          }
        } catch (_) {
          // Keep best-effort cursorPos.
        }

        editor
          .chain()
          .focus()
          .setTextSelection({ from: cursorPos, to: cursorPos })
          .run();

        scrollSelectionIntoViewIfNeeded(editor);
        return true;
      } catch (e) {
        console.error("❌ [selectContentUnderHeader] Error:", e);
        return false;
      }
    },
    [editorRef, language, scrollSelectionIntoViewIfNeeded]
  );

  // --- Insert text or HTML at current selection or cursor, replacing selection ---

  /**
   * TipTap treats a lone outer <p>…</p> as a block. Mid-paragraph macro inserts
   * must unwrap so content lands inline at the caret (not a new line).
   */
  const prepareCaretInsertContent = useCallback((text, { hasSelection, atLineStart }) => {
    let content = String(text ?? "");
    if (hasSelection || atLineStart) {
      content = content.replace(/^\s+/, "");
    }
    if (!hasSelection && !atLineStart) {
      const trimmed = content.trim();
      const singleP = trimmed.match(/^<p(?:\s[^>]*)?>([\s\S]*)<\/p>$/i);
      if (singleP) {
        content = singleP[1];
      }
    }
    return content;
  }, []);

  const insertTextAtCursor = useCallback(
    (text) => {
      if (!text || !text.trim()) return;

      try {
        const editor = editorRef.current?.editor;
        if (!editor || !editor.commands) {
          console.error("Editor not available or not Tiptap");
          return;
        }

        // Read selection before focus (focus can collapse selection in some environments)
        const { state } = editor;
        const { selection } = state;
        const { from, to } = selection;
        const hasSelection = from !== to;

        editor.chain().focus();

        const atLineStart =
          from <= 0 || state.doc.resolve(from).parentOffset === 0;
        const contentToInsert = prepareCaretInsertContent(text, {
          hasSelection,
          atLineStart,
        });

        if (hasSelection) {
          // Replace selected text: delete range, then force cursor to start of deleted range
          // so new text does not merge into the next block (e.g. next heading)
          editor
            .chain()
            .focus()
            .deleteRange({ from, to })
            .setTextSelection(from)
            .insertContent(contentToInsert)
            .run();
        } else {
          // Insert at cursor position (no selection)
          editor
            .chain()
            .focus()
            .insertContent(contentToInsert)
            .run();
        }

        // Update transcript state
        setTranscript(editor.getHTML());
      } catch (e) {
        console.error(
          "Error inserting text at cursor or replacing selection",
          e
        );
      }
    },
    [editorRef, setTranscript, prepareCaretInsertContent]
  );

  // --- Selection utility functions ---

  const getActiveEditor = useCallback(() => {
    const editor = editorRef.current?.editor;
    if (!editor?.view) return null;
    // DOM focus only — editor.commands.focus() can throw during async voice callbacks.
    editor.view.focus();
    return editor;
  }, [editorRef]);

  const applyDocRangeSelection = useCallback(
    (editor, range) => {
      if (!range || range.from >= range.to) return false;

      try {
        const { state, view } = editor;
        const { from, to } = range;
        const selection = TextSelection.create(state.doc, from, to);
        const tr = state.tr.setSelection(selection).scrollIntoView();
        view.dispatch(tr);
        view.focus();
        scrollSelectionIntoViewIfNeeded(editor);
        return true;
      } catch {
        return false;
      }
    },
    [scrollSelectionIntoViewIfNeeded],
  );

  const applyParagraphRangeSelection = useCallback(
    (editor, range) => applyDocRangeSelection(editor, range),
    [applyDocRangeSelection],
  );

  const selectParagraphByOffset = useCallback(
    (editor, offset) => {
      const { selection } = editor.state;
      const docPos = selection.empty
        ? selection.from
        : Math.min(selection.from, selection.to);

      const ranges = listParagraphBlockRanges(editor.state.doc);
      if (!ranges.length) return false;

      let index = findParagraphBlockRangeIndex(ranges, editor.state.doc, docPos);
      if (index < 0) return false;

      index += offset;
      const range = ranges[index];
      if (!range) return false;

      return applyParagraphRangeSelection(editor, range);
    },
    [applyParagraphRangeSelection],
  );

  const applyTextRangeSelection = useCallback(
    (editor, contentStart, range) => {
      if (!range) return false;
      return applyDocRangeSelection(editor, {
        from: contentStart + range.start,
        to: contentStart + range.end,
      });
    },
    [applyDocRangeSelection],
  );

  // Select current field
  const selectCurrentField = useCallback(() => {
    const editor = getActiveEditor();
    if (!editor) return false;

    try {
      const { state } = editor;
      const { selection } = state;
      const { $from } = selection;
      const { doc } = state;

      // Find the current heading (if cursor is in a heading, select it)
      let currentHeading = null;

      // Check if cursor is inside a heading
      for (let depth = $from.depth; depth > 0; depth--) {
        const node = $from.node(depth);
        if (node.type.name.startsWith("heading")) {
          const headingStart = $from.before(depth);
          const headingEnd = headingStart + node.nodeSize;
          return applyDocRangeSelection(editor, {
            from: headingStart,
            to: headingEnd,
          });
        }
      }

      // Find the nearest heading above the cursor
      const headings = [];
      doc.descendants((node, pos) => {
        if (node.type.name.startsWith("heading")) {
          headings.push({
            node,
            pos,
            level: node.attrs.level || 1,
            text: node.textContent || "",
          });
        }
      });

      // Find the heading that contains or is before the cursor
      for (let i = headings.length - 1; i >= 0; i--) {
        const heading = headings[i];
        if (heading.pos <= $from.pos) {
          currentHeading = heading;
          break;
        }
      }

      if (!currentHeading) {
        return false;
      }

      // Select content under this heading
      const targetLevel = currentHeading.level;
      let nextHeadingPos = null;

      for (
        let i = headings.indexOf(currentHeading) + 1;
        i < headings.length;
        i++
      ) {
        const nextHeading = headings[i];
        if (nextHeading.level <= targetLevel) {
          nextHeadingPos = nextHeading.pos;
          break;
        }
      }

      const headingEndPos = currentHeading.pos + currentHeading.node.nodeSize;
      let selectionEnd;

      if (nextHeadingPos !== null) {
        const $nextHeading = doc.resolve(nextHeadingPos);
        if ($nextHeading.depth > 0) {
          const blockStart = $nextHeading.start($nextHeading.depth);
          if (blockStart > headingEndPos) {
            const $beforeBlock = doc.resolve(blockStart - 1);
            if ($beforeBlock.depth > 0) {
              selectionEnd = $beforeBlock.end($beforeBlock.depth);
            } else {
              selectionEnd = blockStart - 1;
            }
          } else {
            selectionEnd = blockStart;
          }
        } else {
          selectionEnd = nextHeadingPos - 1;
        }
      } else {
        selectionEnd = doc.content.size;
      }

      const startPos = Math.max(1, headingEndPos);
      const endPos = Math.max(
        startPos + 1,
        Math.min(selectionEnd, doc.content.size)
      );

      return applyDocRangeSelection(editor, { from: startPos, to: endPos });
    } catch (e) {
      console.error("❌ [selectCurrentField] Error:", e);
      return false;
    }
  }, [getActiveEditor, applyDocRangeSelection]);

  // Select next field
  const selectNextField = useCallback(() => {
    const editor = getActiveEditor();
    if (!editor) return false;

    try {
      const { state } = editor;
      const { selection } = state;
      const { $from } = selection;
      const { doc } = state;

      // Find all headings
      const headings = [];
      doc.descendants((node, pos) => {
        if (node.type.name.startsWith("heading")) {
          headings.push({
            node,
            pos,
            level: node.attrs.level || 1,
            text: node.textContent || "",
          });
        }
      });

      // Find current heading
      let currentHeadingIndex = -1;
      for (let i = headings.length - 1; i >= 0; i--) {
        const heading = headings[i];
        if (heading.pos <= $from.pos) {
          currentHeadingIndex = i;
          break;
        }
      }

      // Get next heading
      let targetHeading;
      if (currentHeadingIndex === -1) {
        // No current heading, start from first
        targetHeading = headings[0];
        if (!targetHeading) {
          return false;
        }
      } else {
        // Get next heading after current
        targetHeading = headings[currentHeadingIndex + 1];
        if (!targetHeading) {
          return false;
        }
      }

      // Select content under the target heading
      const targetLevel = targetHeading.level;
      let nextHeadingPos = null;

      const targetIndex = headings.indexOf(targetHeading);
      for (let i = targetIndex + 1; i < headings.length; i++) {
        const nextHeading = headings[i];
        if (nextHeading.level <= targetLevel) {
          nextHeadingPos = nextHeading.pos;
          break;
        }
      }

      const headingEndPos = targetHeading.pos + targetHeading.node.nodeSize;
      let selectionEnd;

      if (nextHeadingPos !== null) {
        const $nextHeading = doc.resolve(nextHeadingPos);
        if ($nextHeading.depth > 0) {
          const blockStart = $nextHeading.start($nextHeading.depth);
          if (blockStart > headingEndPos) {
            const $beforeBlock = doc.resolve(blockStart - 1);
            if ($beforeBlock.depth > 0) {
              selectionEnd = $beforeBlock.end($beforeBlock.depth);
            } else {
              selectionEnd = blockStart - 1;
            }
          } else {
            selectionEnd = blockStart;
          }
        } else {
          selectionEnd = nextHeadingPos - 1;
        }
      } else {
        selectionEnd = doc.content.size;
      }

      const startPos = Math.max(1, headingEndPos);
      const endPos = Math.max(
        startPos + 1,
        Math.min(selectionEnd, doc.content.size)
      );

      return applyDocRangeSelection(editor, { from: startPos, to: endPos });
    } catch (e) {
      console.error("❌ [selectNextField] Error:", e);
      return false;
    }
  }, [getActiveEditor, applyDocRangeSelection]);

  // Select previous field
  const selectPreviousField = useCallback(() => {
    const editor = getActiveEditor();
    if (!editor) return false;

    try {
      const { state } = editor;
      const { selection } = state;
      const { $from } = selection;
      const { doc } = state;

      // Find all headings
      const headings = [];
      doc.descendants((node, pos) => {
        if (node.type.name.startsWith("heading")) {
          headings.push({
            node,
            pos,
            level: node.attrs.level || 1,
            text: node.textContent || "",
          });
        }
      });

      // Find current heading
      let currentHeadingIndex = -1;
      for (let i = headings.length - 1; i >= 0; i--) {
        const heading = headings[i];
        if (heading.pos <= $from.pos) {
          currentHeadingIndex = i;
          break;
        }
      }

      if (currentHeadingIndex === -1 || currentHeadingIndex === 0) {
        return false;
      }

      // Get previous heading
      const prevHeading = headings[currentHeadingIndex - 1];

      // Select content under the previous heading
      const targetLevel = prevHeading.level;
      let nextHeadingPos = null;

      const targetIndex = headings.indexOf(prevHeading);
      for (let i = targetIndex + 1; i < headings.length; i++) {
        const nextHeading = headings[i];
        if (nextHeading.level <= targetLevel) {
          nextHeadingPos = nextHeading.pos;
          break;
        }
      }

      const headingEndPos = prevHeading.pos + prevHeading.node.nodeSize;
      let selectionEnd;

      if (nextHeadingPos !== null) {
        const $nextHeading = doc.resolve(nextHeadingPos);
        if ($nextHeading.depth > 0) {
          const blockStart = $nextHeading.start($nextHeading.depth);
          if (blockStart > headingEndPos) {
            const $beforeBlock = doc.resolve(blockStart - 1);
            if ($beforeBlock.depth > 0) {
              selectionEnd = $beforeBlock.end($beforeBlock.depth);
            } else {
              selectionEnd = blockStart - 1;
            }
          } else {
            selectionEnd = blockStart;
          }
        } else {
          selectionEnd = nextHeadingPos - 1;
        }
      } else {
        selectionEnd = doc.content.size;
      }

      const startPos = Math.max(1, headingEndPos);
      const endPos = Math.max(
        startPos + 1,
        Math.min(selectionEnd, doc.content.size)
      );

      return applyDocRangeSelection(editor, { from: startPos, to: endPos });
    } catch (e) {
      console.error("❌ [selectPreviousField] Error:", e);
      return false;
    }
  }, [getActiveEditor, applyDocRangeSelection]);

  const selectCurrentSentence = useCallback(() => {
    const editor = getActiveEditor();
    if (!editor) return false;

    try {
      const { $from } = editor.state.selection;
      const block = findCursorTextBlock($from);
      if (!block) {
        return false;
      }

      const range = getCurrentSentenceRange(block.text, block.cursorOffset);
      return applyTextRangeSelection(editor, block.contentStart, range);
    } catch (e) {
      console.error("❌ [selectCurrentSentence] Error:", e);
      return false;
    }
  }, [getActiveEditor, applyTextRangeSelection]);

  const selectNextSentence = useCallback(() => {
    const editor = getActiveEditor();
    if (!editor) return false;

    try {
      const { $from } = editor.state.selection;
      const { doc } = editor.state;
      const block = findCursorTextBlock($from);
      if (!block) {
        return false;
      }

      let range = getNextSentenceRange(block.text, block.cursorOffset);
      if (range) {
        return applyTextRangeSelection(editor, block.contentStart, range);
      }

      const blocks = collectEditorTextBlocks(doc);
      const blockIndex = findBlockIndexAtPos(blocks, $from.pos);
      if (blockIndex < 0) return false;

      for (let i = blockIndex + 1; i < blocks.length; i += 1) {
        range = getFirstSentenceRange(blocks[i].text);
        if (range) {
          return applyTextRangeSelection(
            editor,
            blocks[i].contentStart,
            range,
          );
        }
      }

      return false;
    } catch (e) {
      console.error("❌ [selectNextSentence] Error:", e);
      return false;
    }
  }, [getActiveEditor, applyTextRangeSelection]);

  const selectPreviousSentence = useCallback(() => {
    const editor = getActiveEditor();
    if (!editor) return false;

    try {
      const { $from } = editor.state.selection;
      const { doc } = editor.state;
      const block = findCursorTextBlock($from);
      if (!block) {
        return false;
      }

      let range = getPreviousSentenceRange(block.text, block.cursorOffset);
      if (range) {
        return applyTextRangeSelection(editor, block.contentStart, range);
      }

      const blocks = collectEditorTextBlocks(doc);
      const blockIndex = findBlockIndexAtPos(blocks, $from.pos);
      if (blockIndex < 0) return false;

      for (let i = blockIndex - 1; i >= 0; i -= 1) {
        range = getLastSentenceRange(blocks[i].text);
        if (range) {
          return applyTextRangeSelection(
            editor,
            blocks[i].contentStart,
            range,
          );
        }
      }

      return false;
    } catch (e) {
      console.error("❌ [selectPreviousSentence] Error:", e);
      return false;
    }
  }, [getActiveEditor, applyTextRangeSelection]);

  const selectCurrentParagraph = useCallback(() => {
    const editor = getActiveEditor();
    if (!editor) return false;

    try {
      return selectParagraphByOffset(editor, 0);
    } catch (e) {
      console.error("❌ [selectCurrentParagraph] Error:", e);
      return false;
    }
  }, [getActiveEditor, selectParagraphByOffset]);

  const selectNextParagraph = useCallback(() => {
    const editor = getActiveEditor();
    if (!editor) return false;

    try {
      return selectParagraphByOffset(editor, 1);
    } catch (e) {
      console.error("❌ [selectNextParagraph] Error:", e);
      return false;
    }
  }, [getActiveEditor, selectParagraphByOffset]);

  const selectPreviousParagraph = useCallback(() => {
    const editor = getActiveEditor();
    if (!editor) return false;

    try {
      return selectParagraphByOffset(editor, -1);
    } catch (e) {
      console.error("❌ [selectPreviousParagraph] Error:", e);
      return false;
    }
  }, [getActiveEditor, selectParagraphByOffset]);

  // --- Delete operations ---

  const deleteWord = useCallback(() => {
    const editor = editorRef.current?.editor;
    if (!editor || !editor.commands) {
      console.error("❌ [deleteWord] Editor not available or not Tiptap");
      return false;
    }

    try {
      const { state } = editor;
      const { selection } = state;

      // If there is a selection, delete it (e.g. "מחק מילה" with selected text)
      if (!selection.empty && selection.from !== selection.to) {
        editor.chain().focus().deleteSelection().run();
        setTimeout(() => setTranscript(editor.getHTML()), 0);
        return true;
      }

      const { $from } = selection;

      // Get text from the current node
      let text = "";
      let offset = 0;
      let nodeStart = 0;

      // Try to get text from the current text node
      if ($from.nodeBefore && $from.nodeBefore.isText) {
        text = $from.nodeBefore.text || "";
        offset = text.length;
        nodeStart = $from.pos - offset;
      } else if ($from.parent.isTextblock) {
        // Get text from parent block
        text = $from.parent.textContent || "";
        offset = $from.parentOffset;
        nodeStart = $from.start($from.depth);
      } else {
        // Fallback: get text from document
        const $pos = state.doc.resolve($from.pos);
        text = $pos.nodeAfter?.text || $pos.nodeBefore?.text || "";
        offset = 0;
        nodeStart = $from.pos;
      }

      if (!text || text.length === 0) {
        return false;
      }

      // Find word boundaries - look backwards first
      let wordStart = -1;
      let wordEnd = -1;

      // Check if we're in the middle or end of a word
      for (let i = offset; i >= 0; i--) {
        const char = text[i];
        if (/\w/.test(char)) {
          wordEnd = i + 1;
          // Continue backwards to find start
          for (let j = i - 1; j >= 0; j--) {
            if (!/\w/.test(text[j])) {
              wordStart = j + 1;
              break;
            }
            if (j === 0) {
              wordStart = 0;
              break;
            }
          }
          break;
        }
      }

      // If no word found going backwards, look forward
      if (wordStart === -1) {
        for (let i = offset; i < text.length; i++) {
          const char = text[i];
          if (/\w/.test(char)) {
            wordStart = i;
            // Continue forward to find end
            for (let j = i + 1; j <= text.length; j++) {
              if (j === text.length || !/\w/.test(text[j])) {
                wordEnd = j;
                break;
              }
            }
            break;
          }
        }
      }

      if (wordStart !== -1 && wordEnd !== -1 && wordStart < wordEnd) {
        const from = nodeStart + wordStart;
        const to = nodeStart + wordEnd;

        editor
          .chain()
          .focus()
          .setTextSelection({ from, to })
          .deleteSelection()
          .run();

        // Update transcript asynchronously to avoid React warnings
        setTimeout(() => {
          setTranscript(editor.getHTML());
        }, 0);
        return true;
      }
      return false;
    } catch (e) {
      console.error("❌ [deleteWord] Error:", e);
      return false;
    }
  }, [editorRef, setTranscript]);

  const deleteLine = useCallback(() => {
    const editor = editorRef.current?.editor;
    if (!editor || !editor.commands) {
      console.error("❌ [deleteLine] Editor not available or not Tiptap");
      return false;
    }

    try {
      const { state } = editor;
      const { selection } = state;
      const { $from, $to } = selection;

      // Select current block/node
      const from = $from.start($from.depth);
      const to = $to.end($to.depth);

      editor
        .chain()
        .focus()
        .setTextSelection({ from, to })
        .deleteSelection()
        .run();

      // Update transcript asynchronously to avoid React warnings
      setTimeout(() => {
        setTranscript(editor.getHTML());
      }, 0);
      return true;
    } catch (e) {
      console.error("❌ [deleteLine] Error:", e);
      return false;
    }
  }, [editorRef, setTranscript]);

  const deleteAll = useCallback(() => {
    const editor = editorRef.current?.editor;
    if (!editor || !editor.commands) {
      console.error("❌ [deleteAll] Editor not available or not Tiptap");
      return false;
    }

    try {
      editor
        .chain()
        .focus()
        .clearContent()
        .run();
      // Update transcript asynchronously to avoid React warnings
      setTimeout(() => {
        setTranscript("");
      }, 0);
      return true;
    } catch (e) {
      console.error("❌ [deleteAll] Error:", e);
      return false;
    }
  }, [editorRef, setTranscript]);

  // --- Undo/Redo operations ---

  const undo = useCallback(() => {
    const editor = editorRef.current?.editor;
    if (!editor || !editor.commands) {
      console.error("❌ [undo] Editor not available or not Tiptap");
      return false;
    }

    try {
      if (!editor.can().undo()) return false;
      // Defer so we're not inside React's lifecycle (avoids flushSync warning when triggered from useEffect)
      queueMicrotask(() => {
        const ed = editorRef.current?.editor;
        if (!ed?.commands) return;
        ed.chain().focus().undo().run();
        setTranscript(ed.getHTML());
      });
      return true;
    } catch (e) {
      console.error("❌ [undo] Error:", e);
      return false;
    }
  }, [editorRef, setTranscript]);

  const redo = useCallback(() => {
    const editor = editorRef.current?.editor;
    if (!editor || !editor.commands) {
      console.error("❌ [redo] Editor not available or not Tiptap");
      return false;
    }

    try {
      if (!editor.can().redo()) return false;
      // Defer so we're not inside React's lifecycle (avoids flushSync warning when triggered from useEffect)
      queueMicrotask(() => {
        const ed = editorRef.current?.editor;
        if (!ed?.commands) return;
        ed.chain().focus().redo().run();
        setTranscript(ed.getHTML());
      });
      return true;
    } catch (e) {
      console.error("❌ [redo] Error:", e);
      return false;
    }
  }, [editorRef, setTranscript]);

  // --- Text case operations ---

  const changeTextCase = useCallback(
    (caseType) => {
      const editor = editorRef.current?.editor;
      if (!editor || !editor.commands) {
        console.error("❌ [changeTextCase] Editor not available or not Tiptap");
        return false;
      }

      try {
        const { state } = editor;
        const { selection } = state;
        const { from, to } = selection;

        // If no selection, try to auto-select the current word
        if (from === to) {
          const $pos = selection.$from;
          const node = $pos.node();
          const nodeStart = $pos.start($pos.depth);
          const nodeText = node.textContent || "";
          const offset = $pos.parentOffset;

          // Find word boundaries
          let wordStart = offset;
          let wordEnd = offset;

          // Look backward for word start
          while (wordStart > 0 && /\w/.test(nodeText[wordStart - 1])) {
            wordStart--;
          }

          // Look forward for word end
          while (wordEnd < nodeText.length && /\w/.test(nodeText[wordEnd])) {
            wordEnd++;
          }

          if (wordStart < wordEnd) {
            const actualFrom = nodeStart + wordStart;
            const actualTo = nodeStart + wordEnd;
            const selectedText = nodeText.substring(wordStart, wordEnd);

            let newText;
            switch (caseType) {
              case "uppercase":
                newText = selectedText.toUpperCase();
                break;
              case "lowercase":
                newText = selectedText.toLowerCase();
                break;
              case "capitalize":
                newText = selectedText.replace(/\b\w/g, (char) =>
                  char.toUpperCase()
                );
                break;
              default:
                console.error(
                  `❌ [changeTextCase] Unknown caseType: ${caseType}`
                );
                return false;
            }

            editor
              .chain()
              .focus()
              .setTextSelection({ from: actualFrom, to: actualTo })
              .deleteSelection()
              .insertContent(newText)
              .setTextSelection({
                from: actualFrom,
                to: actualFrom + newText.length,
              })
              .run();

            setTimeout(() => {
              setTranscript(editor.getHTML());
            }, 0);
            return true;
          }
          return false;
        }

        // Handle existing selection
        const selectedText = state.doc.textBetween(from, to);
        if (!selectedText || selectedText.trim().length === 0) {
          return false;
        }

        let newText;
        switch (caseType) {
          case "uppercase":
            newText = selectedText.toUpperCase();
            break;
          case "lowercase":
            newText = selectedText.toLowerCase();
            break;
          case "capitalize":
            newText = selectedText.replace(/\b\w/g, (char) =>
              char.toUpperCase()
            );
            break;
          default:
            console.error(`❌ [changeTextCase] Unknown caseType: ${caseType}`);
            return false;
        }

        editor
          .chain()
          .focus()
          .setTextSelection({ from, to })
          .deleteSelection()
          .insertContent(newText)
          .setTextSelection({ from, to: from + newText.length })
          .run();

        setTimeout(() => {
          setTranscript(editor.getHTML());
        }, 0);
        return true;
      } catch (e) {
        console.error("❌ [changeTextCase] Error:", e);
        return false;
      }
    },
    [editorRef, setTranscript]
  );

  // --- Formatting operations ---

  const toggleBold = useCallback(() => {
    const editor = editorRef.current?.editor;
    if (!editor || !editor.commands) {
      console.error("❌ [toggleBold] Editor not available or not Tiptap");
      return false;
    }

    try {
      editor
        .chain()
        .focus()
        .toggleBold()
        .run();
      setTranscript(editor.getHTML());
      return true;
    } catch (e) {
      console.error("❌ [toggleBold] Error:", e);
      return false;
    }
  }, [editorRef, setTranscript]);

  // --- Tiptap-specific handlers for new line and paragraph ---
  const insertNewLine = useCallback(() => {
    try {
      const editor = editorRef.current?.editor;
      if (!editor || !editor.commands) {
        console.error("❌ [insertNewLine] Editor not available or not Tiptap");
        return false;
      }

      const { state } = editor;
      const { selection } = state;
      const $pos = selection.$from;

      // Check if we're inside a list item
      let isInListItem = false;
      for (let d = $pos.depth; d > 0; d--) {
        if ($pos.node(d).type.name === "listItem") {
          isInListItem = true;
          break;
        }
      }

      // Defer so we're not inside React's lifecycle (avoids flushSync warning when triggered from useEffect)
      queueMicrotask(() => {
        const ed = editorRef.current?.editor;
        if (!ed?.commands) return;
        if (isInListItem) {
          // Split list item (like pressing Enter)
          ed.chain().focus().splitListItem("listItem").run();
        } else {
          // Insert hard break (line break)
          ed.chain().focus().setHardBreak().run();
        }
        setTranscript(ed.getHTML());
      });
      return true;
    } catch (e) {
      console.error("❌ [insertNewLine] Error:", e);
      return false;
    }
  }, [editorRef, setTranscript]);

  const insertNewParagraph = useCallback(() => {
    try {
      const editor = editorRef.current?.editor;
      if (!editor || !editor.commands) {
        console.error(
          "❌ [insertNewParagraph] Editor not available or not Tiptap"
        );
        return false;
      }

      // Defer so we're not inside React's lifecycle (avoids flushSync warning when triggered from useEffect)
      queueMicrotask(() => {
        const ed = editorRef.current?.editor;
        if (!ed?.commands) return;
        // Create new paragraph
        ed.chain().focus().splitBlock().run();
        setTranscript(ed.getHTML());
      });
      return true;
    } catch (e) {
      console.error("❌ [insertNewParagraph] Error:", e);
      return false;
    }
  }, [editorRef, setTranscript]);

  // --- Transcript addition with voice commands integration ---

  const addRecordedTranscript = useCallback(
    (newTranscript) => {
      // A chunk may be only a line break (CRE spoken "new line" → "\n").
      if (!newTranscript || !/\S|\n/.test(newTranscript)) return;

      if (aiEditMode) {
        const local = matchExactUndoRedo(newTranscript, language);
        if (local === "undo") undo();
        else if (local === "redo") redo();
        else onAiEditText?.(newTranscript);
        return;
      }
      if (!commandMode && typeof onEditReportTrigger === "function") {
        const hit = findEditReportTrigger(newTranscript);
        if (hit) {
          // Text before the trigger is normal dictation; the trigger words are not inserted.
          if (hit.before.trim()) addRecordedTranscriptRef.current?.(hit.before);
          onEditReportTrigger(hit.after);
          return;
        }
      }

      const editor = editorRef.current?.editor;
      const hasEditorSelection = (() => {
        if (!editor?.state) return false;
        const { from, to } = editor.state.selection;
        return from !== to;
      })();
      const isSonioxDictation = getSidebarDictationSttMode(language) === "soniox";
      const needsLeadingSpace =
        !isSonioxDictation && needsLeadingDictationSpace(editor);

      // Process transcript using voice commands utility
      // This handles command detection, execution, and text insertion
      processVoiceTranscript(
        newTranscript,
        language,
        anathomy,
        socketRef,
        {
          selectContentUnderHeader,
          selectCurrentSentence,
          selectCurrentParagraph,
          selectNextSentence,
          selectPreviousSentence,
          selectNextParagraph,
          selectPreviousParagraph,
          selectCurrentField,
          selectNextField,
          selectPreviousField,
          deleteWord,
          deleteLine,
          deleteAll,
          undo,
          redo,
          changeTextCase,
          toggleBold,
          insertTextAtCursor,
          insertNewLine,
          insertNewParagraph,
        },
        insertTextAtCursor,
        transcript,
        { macros, onVoiceMacroResult },
        {
          allowTextInsertion: !commandMode,
          hasEditorSelection,
          needsLeadingDictationSpace: needsLeadingSpace,
          preserveSonioxSpacing: isSonioxDictation,
        }
      );
    },
    [
      editorRef,
      transcript,
      insertTextAtCursor,
      insertNewLine,
      insertNewParagraph,
      selectContentUnderHeader,
      selectCurrentSentence,
      selectCurrentParagraph,
      selectNextSentence,
      selectPreviousSentence,
      selectNextParagraph,
      selectPreviousParagraph,
      selectCurrentField,
      selectNextField,
      selectPreviousField,
      deleteWord,
      deleteLine,
      deleteAll,
      undo,
      redo,
      changeTextCase,
      toggleBold,
      socketRef,
      language,
      anathomy,
      macros,
      onVoiceMacroResult,
      commandMode,
      aiEditMode,
      onAiEditText,
      onEditReportTrigger,
    ]
  );

  addRecordedTranscriptRef.current = addRecordedTranscript;

  // --- Mic control and effects ---

  const isRecordingRef = useRef(isRecording);
  isRecordingRef.current = isRecording;

  const stopMic = useCallback(() => {
    if (isRecordingRef.current) {
      stopRecording();
    }
    setIsMicActive(false);
    resetEditorTranscript();
  }, [stopRecording, resetEditorTranscript]);

  const startMic = useCallback(async () => {
    if (isRecordingRef.current) return false;
    resetEditorTranscript();
    const started = await toggleRecording();
    if (!started) return false;
    setIsMicActive(true);
    return true;
  }, [resetEditorTranscript, toggleRecording]);

  const handleMicClick = useCallback(async () => {
    if (isRecordingRef.current) {
      stopMic();
      return true;
    }
    return await startMic();
  }, [startMic, stopMic]);

  useEffect(() => {
    handleStreamExpiredRef.current = () => {
      if (isRecording) {
        stopRecording();
      }
      setIsMicActive(false);
    };
  }, [isRecording, stopRecording]);

  const resetTranscriptState = useCallback(() => {
    const editor = editorRef.current?.editor;
    try {
      if (editor?.commands) {
        editor.chain().focus().clearContent().run();
      }
    } catch (e) {
      console.error("Error clearing editor on reset:", e);
    }
    setTranscript("");
  }, [editorRef]);

  useEffect(() => {
    if (initialContent && !isTranscriptInitialized) {
      setTranscript(initialContent);
      setIsTranscriptInitialized(true);
    }
  }, [initialContent, isTranscriptInitialized]);

  useEffect(() => {
    try {
      const editor = editorRef.current?.editor;
      if (editor && initialContent && transcript !== initialContent) {
        editor.setContents(initialContent);
        setTranscript(initialContent);
        setIsTranscriptInitialized(true);
      }
    } catch (err) {
      console.error("Error syncing editor content:", err);
    }
  }, [editorRef, initialContent, transcript]);

  useEffect(() => {
    let timeoutId;
    try {
      const editor = editorRef.current?.editor;
      if (editor && editor.getContents() !== transcript) {
        timeoutId = setTimeout(() => {
          editor.setContents(transcript);
        }, 50);
      }
    } catch (err) {
      console.error(
        "Error syncing editor with external transcript change:",
        err
      );
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [transcript, editorRef]);

  return {
    transcript,
    setTranscript,
    isMicActive,
    streamingTranscript,
    handleMicClick,
    startMic,
    stopMic,
    insertTextAtCursor,
    addRecordedTranscript,
    resetTranscriptState,
    selectContentUnderHeader,
    selectCurrentField,
    selectNextField,
    selectPreviousField,
    selectCurrentSentence,
    selectNextSentence,
    selectPreviousSentence,
    selectCurrentParagraph,
    selectNextParagraph,
    selectPreviousParagraph,
    deleteWord,
    deleteLine,
    deleteAll,
    undo,
    redo,
    changeTextCase,
    toggleBold,
  };
};

export default useRteWithSpeech;
